package com.dotdrives.client

import com.dotdrives.core.uuidV7
import com.dotdrives.model.OrgInvite
import com.dotdrives.model.OrgMember
import com.dotdrives.model.OrgSummary
import com.dotdrives.model.WorkflowFailure
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.exception.PostgrestRestException
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.query.Order
import kotlinx.serialization.Serializable
import kotlinx.serialization.SerialName
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlin.time.Instant

class OrgClient(
    private val client: SupabaseClient = SupabaseProvider.client,
    private val authClient: AuthClient = AuthClient(client),
) {
    /** Validate URL-scoped access and establish audited support access when authorized. */
    suspend fun openDeepLink(orgId: String) {
        // Google staff sessions may still need server-side provider verification after OAuth.
        // Use the same admission flow as the admin console before entering an organization.
        val staff = AdminClient(client).entry()
        if (staff?.role == "super_admin" && !staff.authorized) {
            throw WorkflowFailure("unauthorized", "Sign in again to start an audited support session for this organization.")
        }
        try {
            client.postgrest.rpc("org_open_deep_link", buildJsonObject { put("p_org", orgId) })
        } catch (error: PostgrestRestException) {
            throw WorkflowFailure(error.code, when (error.code) {
                "42501" -> "You do not have access to this organization, or it is unavailable."
                "23514" -> "This organization is paused. Reactivate it before starting a write support session."
                "PGRST301", "PGRST302", "PGRST303" -> "Your sign-in has expired. Sign in again to open this organization."
                "22P02", "22023" -> "This organization link is invalid. Check the link and try again."
                else -> "This organization could not be opened. Try again."
            })
        }
    }

    suspend fun myOrgs(): List<OrgSummary> {
        val orgs = client.from("orgs")
            .select()
            .decodeList<OrgRow>()
        val rolesByOrgId = client.from("org_members")
            .select {
                filter {
                    eq("user_id", authClient.requireCurrentUserId())
                }
            }
            .decodeList<OrgRoleRow>()
            .associate { it.orgId to it.role }
        return orgs.map { org ->
            val access = AdminClient(client).orgAccess(org.id)
            OrgSummary(
                id = org.id,
                name = org.name,
                kind = org.kind,
                role = if(access.supportMode != null) { if(access.supportMode == "write") "owner" else "member" } else rolesByOrgId[org.id] ?: "member",
                fiscalStartMonth = org.fiscalStartMonth,
                fiscalStartDay = org.fiscalStartDay,
                readOnly = access.readOnly,
                supportSession = access.session,
            )
        }
    }

    suspend fun createOrg(name: String, kind: String): OrgSummary {
        val id = uuidV7()
        client.postgrest.rpc(
            function = "create_org",
            parameters = buildJsonObject {
                put("p_id", id)
                put("p_name", name)
                put("p_kind", kind)
            },
        )
        return OrgSummary(id, name, kind, role = "owner")
    }

    suspend fun updateOrg(orgId: String, name: String, kind: String): OrgSummary {
        client.from("orgs").update(
            OrgUpdate(name = name, kind = kind),
        ) {
            filter {
                eq("id", orgId)
            }
        }
        return OrgSummary(orgId, name, kind, role = "owner")
    }

    suspend fun members(orgId: String): List<OrgMember> {
        val members = client.from("org_members")
            .select {
                filter {
                    eq("org_id", orgId)
                }
            }
            .decodeList<OrgMemberRow>()
        val namesByUserId = if (members.isEmpty()) {
            emptyMap()
        } else {
            client.from("profiles")
                .select {
                    filter {
                        isIn("user_id", members.map(OrgMemberRow::userId))
                    }
                }
                .decodeList<MemberProfileRow>()
                .associate { profile ->
                    profile.userId to listOf(profile.firstName, profile.lastName)
                        .filter(String::isNotBlank)
                        .joinToString(" ")
                }
        }
        val acceptedEmails = if (members.any { namesByUserId[it.userId].isNullOrBlank() }) {
            invites(orgId).filter { it.acceptedBy != null }.associate { it.acceptedBy to it.email }
        } else emptyMap()
        val current = authClient.currentSession()
        return members.map { member ->
            OrgMember(
                userId = member.userId,
                displayName = namesByUserId[member.userId]
                    ?.takeIf(String::isNotBlank)
                    ?: acceptedEmails[member.userId]
                    ?: current?.email?.takeIf { current.userId == member.userId }
                    ?: "Teammate · profile incomplete",
                role = member.role,
                deactivatedAt = member.deactivatedAt,
            )
        }
    }

    suspend fun invite(orgId: String, email: String, role: String) {
        try {
            client.from("org_invites").insert(
                OrgInviteInsert(
                    id = uuidV7(),
                    orgId = orgId,
                    email = email.trim().lowercase(),
                    role = role,
                    createdBy = authClient.requireCurrentUserId(),
                ),
            )
        } catch (error: PostgrestRestException) {
            val isDuplicateInvite = error.code == UNIQUE_VIOLATION &&
                error.message?.contains(INVITE_EMAIL_CONSTRAINT) == true
            if (!isDuplicateInvite) throw error
        }
    }

    /**
     * Pending invites for the users-and-roles screen. Gated on
     * `has_perm(org_id,'org.write')` on read (docs/schema.md), the same permission that gates
     * sending one — a `field` member sees an empty list here, not an error, which
     * SupabaseOrgRepository.invites and the controller both treat as a normal RLS result.
     */
    suspend fun invites(orgId: String): List<OrgInvite> =
        client.from("org_invites")
            .select {
                order("created_at", Order.DESCENDING)
                filter {
                    eq("org_id", orgId)
                }
            }
            .decodeList<OrgInviteRow>()
            .map { it.toModel() }

    private companion object {
        const val UNIQUE_VIOLATION = "23505"
        const val INVITE_EMAIL_CONSTRAINT = "org_invites_org_email_unique"
    }
}

@Serializable
private data class OrgRow(
    val id: String,
    val name: String,
    val kind: String,
    @SerialName("fiscal_start_month") val fiscalStartMonth: Int = 1,
    @SerialName("fiscal_start_day") val fiscalStartDay: Int = 1,
)

@Serializable
private data class OrgRoleRow(
    @SerialName("org_id")
    val orgId: String,
    val role: String,
)

@Serializable
private data class OrgMemberRow(
    @SerialName("deactivated_at") val deactivatedAt: String? = null,
    @SerialName("user_id")
    val userId: String,
    val role: String,
)

@Serializable
private data class MemberProfileRow(
    @SerialName("user_id")
    val userId: String,
    @SerialName("first_name")
    val firstName: String,
    @SerialName("last_name")
    val lastName: String,
)

@Serializable
private data class OrgUpdate(
    val name: String,
    val kind: String,
)

@Serializable
private data class OrgInviteInsert(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    val email: String,
    val role: String,
    @SerialName("created_by")
    val createdBy: String,
)

@Serializable
private data class OrgInviteRow(
    @SerialName("accepted_by") val acceptedBy: String? = null,
    @SerialName("expires_at") val expiresAt: Instant? = null,
    @SerialName("accepted_at") val acceptedAt: Instant? = null,
    @SerialName("revoked_at") val revokedAt: Instant? = null,
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    val email: String,
    val role: String,
    @SerialName("created_by")
    val createdBy: String,
    @SerialName("created_at")
    val createdAt: Instant,
) {
    fun toModel() = OrgInvite(
        id = id,
        orgId = orgId,
        email = email,
        role = role,
        createdBy = createdBy,
        createdAt = createdAt,
        expiresAt = expiresAt, acceptedAt = acceptedAt, revokedAt = revokedAt, acceptedBy = acceptedBy,
    )
}
